import { useState } from "react";
import axios from "axios";
import { CheckCircle2, Loader2, MessageCircle, Send } from "lucide-react";
import { API, waLink } from "@/lib/site";

const TYPES = ["Business Website", "E-Commerce Website", "Clinic / Hospital Website", "Salon / Beauty Website", "Restaurant / Cafe Website", "Real Estate Website", "Educational Institute Website", "Landing Page", "Website Redesign", "Website Maintenance", "Other"];
const BUDGETS = ["Under ₹10,000", "₹10,000 – ₹25,000", "₹25,000 – ₹50,000", "₹50,000 – ₹1,00,000", "Above ₹1,00,000", "Not sure yet"];
const EMPTY = { full_name: "", mobile: "", email: "", business_name: "", website_type: "", budget_range: "", requirements: "", hp: "" };

const validate = (f) => {
  const e = {};
  if (f.full_name.trim().length < 2) e.full_name = "Please enter your full name";
  if (!/^(\+?91)?[6-9]\d{9}$/.test(f.mobile.replace(/[\s\-()]/g, ""))) e.mobile = "Enter a valid 10-digit mobile number";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(f.email.trim())) e.email = "Enter a valid email address";
  if (!f.website_type) e.website_type = "Select a website type";
  if (!f.budget_range) e.budget_range = "Select a budget range";
  if (f.requirements.trim().length < 10) e.requirements = "Please describe your project (min 10 characters)";
  return e;
};

const waSummary = (f) =>
  `Hello Pratap Group, I just submitted an enquiry on your website.\n\nName: ${f.full_name}\nMobile: ${f.mobile}\nEmail: ${f.email}\nBusiness: ${f.business_name || "-"}\nWebsite type: ${f.website_type}\nBudget: ${f.budget_range}\nRequirements: ${f.requirements}`;

const inputCls = "w-full rounded-xl border border-white/10 bg-[#070c1a] px-4 py-3.5 text-sm text-white placeholder:text-slate-500 outline-none transition-[border-color,box-shadow] duration-300 focus:border-[var(--gold)] focus:shadow-[0_0_0_4px_rgba(232,182,76,.12)]";

const Field = ({ label, error, children }) => (
  <label className="block">
    <span className="mb-2 block text-xs font-medium uppercase tracking-wider text-slate-300">{label}</span>
    {children}
    {error && <span className="mt-1.5 block text-xs text-red-400">{error}</span>}
  </label>
);

export const ContactForm = () => {
  const [f, setF] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [serverError, setServerError] = useState("");
  const [sent, setSent] = useState(null);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(f);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    const popup = window.open("", "_blank");
    setStatus("loading");
    setServerError("");
    try {
      await axios.post(`${API}/enquiries`, f);
      const url = waLink(waSummary(f));
      if (popup) popup.location.href = url;
      setSent({ ...f, url });
      setF(EMPTY);
      setStatus("success");
    } catch (err) {
      popup?.close();
      const d = err.response?.data?.detail;
      setServerError(Array.isArray(d) ? d.map((x) => x?.msg?.replace("Value error, ", "")).join(". ") : typeof d === "string" ? d : "Could not send your enquiry. Please try again or contact us on WhatsApp.");
      setStatus("error");
    }
  };

  if (status === "success" && sent) {
    return (
      <div data-testid="contact-form-success" className="flex h-full flex-col items-start justify-center py-10">
        <CheckCircle2 className="h-14 w-14 text-[var(--gold)]" />
        <h3 className="font-display mt-6 text-3xl font-semibold text-white">Thank you, {sent.full_name.split(" ")[0]}!</h3>
        <p className="mt-3 max-w-md text-slate-300">Your enquiry has been received. The Pratap Group team will get back to you shortly. A WhatsApp chat with your enquiry details has also been opened for faster response.</p>
        <div className="mt-8 flex flex-wrap gap-3">
          <a href={sent.url} target="_blank" rel="noopener noreferrer" data-testid="contact-success-whatsapp-button" className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-sm font-semibold text-black">
            <MessageCircle className="h-4 w-4" /> Send details on WhatsApp
          </a>
          <button onClick={() => { setStatus("idle"); setSent(null); }} data-testid="contact-success-new-button" className="rounded-full border border-white/15 px-6 py-3 text-sm font-semibold text-white">New enquiry</button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate data-testid="contact-form" className="grid gap-5 sm:grid-cols-2">
      <input type="text" tabIndex={-1} autoComplete="off" value={f.hp} onChange={set("hp")} className="hidden" aria-hidden="true" />
      <Field label="Full Name *" error={errors.full_name}><input data-testid="contact-input-name" className={inputCls} value={f.full_name} onChange={set("full_name")} placeholder="Your full name" /></Field>
      <Field label="Mobile Number *" error={errors.mobile}><input data-testid="contact-input-mobile" type="tel" className={inputCls} value={f.mobile} onChange={set("mobile")} placeholder="10-digit mobile" /></Field>
      <Field label="Email Address *" error={errors.email}><input data-testid="contact-input-email" type="email" className={inputCls} value={f.email} onChange={set("email")} placeholder="you@email.com" /></Field>
      <Field label="Business Name" error={errors.business_name}><input data-testid="contact-input-business" className={inputCls} value={f.business_name} onChange={set("business_name")} placeholder="Your business" /></Field>
      <Field label="Website Type *" error={errors.website_type}>
        <select data-testid="contact-select-type" className={inputCls} value={f.website_type} onChange={set("website_type")}>
          <option value="">Select website type</option>
          {TYPES.map((t) => <option key={t} value={t} className="bg-[#0a1226]">{t}</option>)}
        </select>
      </Field>
      <Field label="Budget Range *" error={errors.budget_range}>
        <select data-testid="contact-select-budget" className={inputCls} value={f.budget_range} onChange={set("budget_range")}>
          <option value="">Select budget range</option>
          {BUDGETS.map((t) => <option key={t} value={t} className="bg-[#0a1226]">{t}</option>)}
        </select>
      </Field>
      <div className="sm:col-span-2">
        <Field label="Project Requirements *" error={errors.requirements}>
          <textarea data-testid="contact-input-requirements" rows={4} className={`${inputCls} resize-none`} value={f.requirements} onChange={set("requirements")} placeholder="Tell us about your business and the website you need" />
        </Field>
      </div>
      {serverError && <p data-testid="contact-form-error" className="text-sm text-red-400 sm:col-span-2">{serverError}</p>}
      <button type="submit" disabled={status === "loading"} data-testid="contact-submit-button" className="btn-gold btn-shine inline-flex items-center justify-center gap-2 rounded-full py-4 text-sm font-semibold transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-70 sm:col-span-2">
        {status === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        {status === "loading" ? "Sending..." : "Request a Free Quote"}
      </button>
    </form>
  );
};
